import { Dispatch } from 'redux';
import types from 'redux/actions/actionTypes';
import { axiosV2 } from 'interceptors/axios';

import { IncentiveOffer } from 'modules/incentivesMarket/types';
import { PublisherAuditClientContext } from 'modules/incentivesMarket/utils/collectPublisherAuditContext';
import { updateIncentiveSocietyData } from 'modules/incentivesMarket/utils/updateIncentiveSocietyData';

import { decryptResponse } from 'utils/token';
import { dispatchError } from 'utils/dispatchError';
import { ApiQueryParams } from 'types/ApiQueryParams';

type GetIncentiveOffersProps = {
  societyId: string;
  params?: ApiQueryParams;
  signal?: AbortSignal;
  skipDispatch?: boolean;
};

type IncentiveOfferApiAddResponse = {
  incentiveOffer: IncentiveOffer;
  incentiveOffersCount: {
    buy: number;
    sell: number;
    total: number;
  };
};

type IncentiveOfferApiGetAllResponse = {
  data: IncentiveOffer[];
  total: number;
};

type IncentiveOfferApiMutationResponse = {
  incentiveOffer: IncentiveOffer;
  incentiveOffersCount?: {
    buy: number;
    sell: number;
    total: number;
  };
};

type UpdateIncentiveOfferPayload = Partial<
  Pick<
    IncentiveOffer,
    | 'unitPrice'
    | 'startDate'
    | 'endDate'
    | 'isPartialAmountEnabled'
    | 'minUnitsPerAcceptance'
    | 'isSellerNameHidden'
    | 'emailSubsidiary'
    | 'documents'
  >
> & {
  unitsCount?: Pick<IncentiveOffer['unitsCount'], 'total'>;
};

type AddIncentiveOfferPayload = Partial<IncentiveOffer> & {
  publisherAuditContext?: PublisherAuditClientContext;
};

export const addIncentiveOffer =
  (incentiveOfferData: AddIncentiveOfferPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const body = { ...incentiveOfferData };
      const { data } = await axiosV2.post('/incentiveOffers', body);
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferApiAddResponse;

      dispatch({
        type: types.CREATE_INCENTIVE_OFFER,
        incentiveOffer: decodedData?.incentiveOffer,
      });

      updateIncentiveSocietyData(decodedData, dispatch);

      return decodedData?.incentiveOffer;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };

export const getIncentiveOffers =
  ({
    societyId,
    params = {},
    signal,
    skipDispatch = false,
  }: GetIncentiveOffersProps) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get('/incentiveOffers', {
        params: {
          societyId,
          ...params,
        },
        // signal,
      });

      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferApiGetAllResponse;

      if (!skipDispatch) {
        dispatch({
          type: types.GET_INCENTIVE_OFFERS,
          incentiveOffers: decodedData.data,
        });
      }

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
    }
  };

export const getIncentiveOffer =
  (incentiveOfferId: string, params?: ApiQueryParams) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.get(
        `/incentiveOffers/${incentiveOfferId}`,
        {
          params,
        }
      );
      const decodedData = (await decryptResponse(data)) as IncentiveOffer;

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };

export const updateIncentiveOffer =
  (incentiveOfferId: string, payload: UpdateIncentiveOfferPayload) =>
  async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.put(
        `/incentiveOffers/${incentiveOfferId}`,
        payload
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferApiMutationResponse;

      dispatch({
        type: types.UPDATE_INCENTIVE_OFFER,
        incentiveOffer: decodedData?.incentiveOffer,
      });

      return decodedData?.incentiveOffer;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };

export const cancelIncentiveOffer =
  (incentiveOfferId: string) => async (dispatch: Dispatch) => {
    try {
      const { data } = await axiosV2.delete(
        `/incentiveOffers/${incentiveOfferId}`
      );
      const decodedData = (await decryptResponse(
        data
      )) as IncentiveOfferApiMutationResponse;

      dispatch({
        type: types.DELETE_INCENTIVE_OFFER,
        incentiveOfferId: decodedData?.incentiveOffer?._id || incentiveOfferId,
      });

      updateIncentiveSocietyData(decodedData, dispatch);

      return decodedData;
    } catch (error) {
      dispatchError(error, dispatch);
      throw error;
    }
  };
